import { formatNumber } from "@/utils/format-fr";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, PauseCircle, PlayCircle, Undo2 } from "lucide-react";
import { Checklist, denseTable, EmptyValue, FieldGrid, Section, StateBlock, StatusBadge } from "@/components/layout-2/ds";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CampaignLiveContent, CampaignLiveSection } from "@/components/dialer/campaigns/campaign-live-section";
import { DeployTarget } from "@/components/dialer/versions/deploy-dialog";
import { RollbackDialog } from "@/components/dialer/versions/rollback-dialog";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import type { BadgeValue } from "@/types/dynamic-detail";
import { Deployment, parseVersionLabel, ReadinessIssue } from "@/types/dialer/workflow";
import { workflowVersionPath } from "@/utils/dialer-breadcrumbs";
import { formatParis, humanizeToken } from "@/utils/dialer-datetime";

/** One slot of the campaign as `readiness.deployed_slots` serves it. */
export type DeployedSlot = {
    version_id: number;
    version: number;
    /** `v{n}`. */
    version_label: string;
    /** `{workflow name} v{n}`. */
    label: string;
    workflow_id: number;
    workflow_code: string;
    workflow_name: string;
    status: string;
    /** Version status as served: render it, never recolor it. */
    status_badge?: BadgeValue | null;
};

export type CallerNumberSummary = {
    /** Distinct active numbers across the pools below. */
    count: number;
    pools: { code: string; name: string; exists: boolean; is_active: boolean; count: number }[];
};

/** What `CampaignOperationsTab` serializes. Declared here: it is a tab payload shape. */
export type CampaignOperationsContent = {
    campaign: {
        id: number;
        code: string;
        name: string;
        status: string;
        /** `status` as served by the tab: render it, never recolor it. */
        status_badge?: BadgeValue | null;
        is_active: boolean;
        is_dispatchable: boolean;
        active_cards: number;
        main_workflow_id: number | null;
        callback_workflow_id: number | null;
        /** What the main slot runs, readable: absent from older payloads, `null` when empty or not readable. */
        main_workflow?: DeployedSlot | null;
        callback_workflow?: DeployedSlot | null;
        /** Active caller numbers the deployed graphs may present, and the pools their call steps name. */
        caller_numbers?: CallerNumberSummary;
    };
    readiness: {
        can_activate: boolean;
        blockers: ReadinessIssue[];
        warnings: ReadinessIssue[];
    };
    deployments: Deployment[];
    permissions: { edit?: boolean; deploy?: boolean; simulate?: boolean };
    live: CampaignLiveContent;
};

const linkClass = "text-blue-700 hover:text-blue-900 hover:underline";

/** A deployed slot as a link to its version graph, with the version status beside it. */
function SlotValue({ slot }: { slot: DeployedSlot }) {
    return (
        <span className="flex flex-wrap items-center gap-2">
            <NavLink className={linkClass} to={workflowVersionPath(slot.workflow_code, slot.version)}>
                {slot.label}
            </NavLink>
            <StatusBadge value={slot.status_badge} />
        </span>
    );
}

function CallerNumbersValue({ summary }: { summary: CallerNumberSummary }) {
    const pools = summary.pools.map(pool => pool.name).join(", ");
    return (
        <span className="tabular-nums" title={pools || undefined}>
            {formatNumber(summary.count)}
            {pools && <span className="text-gray-500"> · {pools}</span>}
        </span>
    );
}

/** Slot columns, so an issue about one points at the slot tabs rather than at settings. */
const SLOT_FIELDS = ["main_workflow_version", "callback_workflow_version"];

/** Where a readiness issue is fixed: the slot tab for a slot column, the settings otherwise. */
function issueLink(campaignId: number, field: string): string {
    if (SLOT_FIELDS.includes(field)) {
        return `/dialer-campaigns/${campaignId}/${field === "callback_workflow_version" ? "callback-workflow" : "main-workflow"}`;
    }
    return `/dialer-campaigns/${campaignId}/informations`;
}

/** Dense table, same look as `ObjectListTableResizable layout="dense"`: gray header, compact rows. */
export type OperationsPanelProps = {
    content: CampaignOperationsContent;
    /** Refetches the tab and the record header once service state or history moves. */
    onMutate?: () => void;
};

type ServiceChangeError = {
    status?: number;
    response?: {
        status?: number;
        json?: () => Promise<unknown>;
    };
    json?: unknown;
};

function isErrorBody(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

/**
 * Readiness, put-in-service / pause, and the deployment history of one campaign.
 *
 * This is the part of the old `/outils/campagnes/<code>` console that is about the
 * campaign rather than about one slot. Slot deploy lives on `WorkflowSlotPanel`; the
 * settings live on the Informations tab. Writing still goes through the dialer
 * endpoints — activate, pause, rollback — so this panel is a second door, not a second
 * path.
 */
export function OperationsPanel({ content, onMutate }: OperationsPanelProps) {
    const { campaign, readiness, deployments, permissions, live } = content;
    const [changingService, setChangingService] = useState(false);
    const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
    const [refusedBlockers, setRefusedBlockers] = useState<ReadinessIssue[]>([]);
    const [rollback, setRollback] = useState<{
        target: DeployTarget;
        workflowCode: string;
        versionId: number;
        versionNumber: number;
    } | null>(null);

    const mayChangeService = permissions?.deploy !== false;
    const issues = refusedBlockers.length > 0 ? refusedBlockers : readiness.blockers;

    const changeService = async (intent: "activate" | "pause") => {
        setChangingService(true);
        setRefusalMessage(null);
        setRefusedBlockers([]);
        try {
            await DataService.client.url(`/dialer/campaigns/${campaign.code}/${intent}/`).post({}).res();
            toast.success(intent === "activate" ? "Campaign put in service" : "Campaign paused");
            onMutate?.();
        } catch (error: unknown) {
            const failure = error as ServiceChangeError;
            const status = failure.status ?? failure.response?.status;
            let body: Record<string, unknown> = {};
            if (isErrorBody(failure.json)) {
                body = failure.json;
            } else if (typeof failure.response?.json === "function") {
                const parsed = await failure.response.json().catch(() => null);
                if (isErrorBody(parsed)) body = parsed;
            }
            if (status === 409 && Array.isArray(body.blockers)) {
                setRefusedBlockers(body.blockers as ReadinessIssue[]);
            } else {
                setRefusalMessage(typeof body.error === "string" ? body.error : "The service change was refused.");
            }
        } finally {
            setChangingService(false);
        }
    };

    const serviceButton = mayChangeService ? (
        <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => changeService(campaign.is_active ? "pause" : "activate")} disabled={changingService}>
            {changingService ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : campaign.is_active ? <PauseCircle className="mr-1 h-4 w-4" /> : <PlayCircle className="mr-1 h-4 w-4" />}{" "}
            {campaign.is_active ? "Pause" : "Put in service"}
        </Button>
    ) : undefined;

    return (
        <div>
            {refusalMessage && (
                <Alert variant="destructive">
                    <AlertTitle>Service change refused</AlertTitle>
                    <AlertDescription>{refusalMessage}</AlertDescription>
                </Alert>
            )}

            <Section icon="Gauge" title="Readiness" actions={serviceButton}>
                <FieldGrid
                    items={[
                        { label: "Status", value: <StatusBadge value={campaign.status_badge} /> },
                        { label: "Active cards", value: <span className="tabular-nums">{formatNumber(campaign.active_cards)}</span> },
                        { label: "Dispatchable", value: campaign.is_dispatchable ? "Yes" : "No" },
                        { label: "Main workflow", value: campaign.main_workflow ? <SlotValue slot={campaign.main_workflow} /> : null },
                        { label: "Callback workflow", value: campaign.callback_workflow ? <SlotValue slot={campaign.callback_workflow} /> : null },
                        { label: "Caller numbers", value: campaign.caller_numbers ? <CallerNumbersValue summary={campaign.caller_numbers} /> : null }
                    ]}
                />

                {issues.length === 0 ? (
                    <p className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="h-4 w-4 text-green-800" />
                        {campaign.is_active ? "This campaign is in service." : "Nothing is missing: this campaign can be activated."}
                    </p>
                ) : (
                    <Checklist
                        items={issues.map(issue => ({
                            code: issue.code + (issue.field ?? ""),
                            label: issue.message,
                            done: false,
                            hint: issue.field ? (
                                <NavLink className={linkClass} to={issueLink(campaign.id, issue.field)}>
                                    {SLOT_FIELDS.includes(issue.field) ? "Open the slot" : "Fix it in the settings"}
                                </NavLink>
                            ) : undefined
                        }))}
                    />
                )}
                {readiness.warnings.map(warning => (
                    <Alert key={warning.code} variant="warning">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{warning.message}</AlertDescription>
                    </Alert>
                ))}
            </Section>

            {live && <CampaignLiveSection campaignId={campaign.id} initial={live} />}

            <Section icon="History" title="Deployment history">
                {deployments.length === 0 ? (
                    <StateBlock message="No deployment recorded for this campaign yet." />
                ) : (
                    <Table>
                        <TableHeader className={denseTable.header}>
                            <TableRow className={denseTable.headerRow}>
                                <TableHead className={denseTable.head}>When</TableHead>
                                <TableHead className={denseTable.head}>Slot</TableHead>
                                <TableHead className={denseTable.head}>Version</TableHead>
                                <TableHead className={denseTable.head}>Cards in flight</TableHead>
                                <TableHead className={denseTable.head}>By</TableHead>
                                <TableHead className={denseTable.head} />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {deployments.map(deployment => {
                                const installed = parseVersionLabel(deployment.to_version_label);
                                const currentId = deployment.slot === "callback" ? campaign.callback_workflow_id : campaign.main_workflow_id;
                                const isCurrent = deployment.to_version === currentId;
                                return (
                                    <TableRow key={deployment.id}>
                                        <TableCell className={cn(denseTable.cell, "whitespace-nowrap tabular-nums")}>{formatParis(deployment.created_at)}</TableCell>
                                        <TableCell className={denseTable.cell}>{humanizeToken(deployment.slot)}</TableCell>
                                        <TableCell className={denseTable.cell}>
                                            <span className="flex flex-wrap items-center gap-1">
                                                {deployment.from_version_label && <span className="text-gray-500">{deployment.from_version_label} →</span>}
                                                <span>{deployment.to_version_label ?? `#${deployment.to_version}`}</span>
                                                {deployment.is_rollback && <Badge tone="warning">Rollback</Badge>}
                                            </span>
                                        </TableCell>
                                        <TableCell className={denseTable.cell}>
                                            {humanizeToken(deployment.in_flight_policy)}
                                            {deployment.in_flight_counts ? ` · ${formatNumber(deployment.in_flight_counts.active_cards)} cards` : ""}
                                        </TableCell>
                                        <TableCell className={denseTable.cell}>{deployment.created_by_name ?? <EmptyValue />}</TableCell>
                                        <TableCell className={cn(denseTable.cell, "text-right")}>
                                            {!isCurrent && installed && permissions?.deploy !== false && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 bg-white"
                                                    onClick={() =>
                                                        setRollback({
                                                            target: {
                                                                campaign_code: campaign.code,
                                                                campaign_name: campaign.name,
                                                                slot: deployment.slot
                                                            },
                                                            workflowCode: installed.code,
                                                            versionId: deployment.to_version,
                                                            versionNumber: installed.version
                                                        })
                                                    }
                                                >
                                                    <Undo2 className="mr-1 h-4 w-4" /> Roll back to it
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Section>

            {rollback && (
                <RollbackDialog
                    open
                    onOpenChange={open => !open && setRollback(null)}
                    workflowCode={rollback.workflowCode}
                    workflowName={rollback.workflowCode}
                    versionId={rollback.versionId}
                    versionNumber={rollback.versionNumber}
                    targets={[rollback.target]}
                    onDone={() => {
                        setRollback(null);
                        onMutate?.();
                    }}
                />
            )}
        </div>
    );
}
